import React, {useState, useEffect, useContext, useRef} from "react";
import useAxios from "../../utils/useAxios";
import {useTranslation} from "react-i18next";
import FeatherIcon from "feather-icons-react";
import ErrorMessage from "../../components/ErrorMessage";
import SelectInput from "../../components/SelectInput";
import {COUNTRY_OPTIONS} from "../../constants";
import {toast} from "react-toastify";
import FormImageUpload from "../../components/CustomModel/FormImageUpload";
import Skeleton from "react-loading-skeleton";
import AuthContext from "../../contexts/AuthContext";
import useSubmitLock from "../../hooks/useSubmitLock";

const initialData = {
    name: "",
    country: "UA",
    city: "",
    address: "",
    phone: "",
    creator: "",
    is_intennis_db: false
};

const LocationForm = ({locationId = null, onSuccess, onClose, img, width, height}) => {
    const { user } = useContext(AuthContext);
    const {t} = useTranslation();
    const api = useAxios();
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [file, setFile] = useState(img);
    const [loader, setLoader] = useState(true);
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    useEffect(() => {
        if (locationId) {
            api.get(`/api/v1.0/locations/${locationId}/`)
                .then(res => {
                    setFormData(res.data);
                    if (res.data.image) {
                        setFile(res.data.image);
                    }
                })
                .catch(err => console.error(err))
                .finally(() => {
                setTimeout(() => setLoader(false), 200);
            });
        } else {
            setFormData(initialData);
            setFile(null);
            setLoader(false);
        }
    }, [locationId]);

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleSubmit = wrapSubmit(async () => {
        try {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (key === "image") return;
                if (value !== null && value !== undefined) data.append(key, value);
            });

            if (file instanceof File || (file?.file instanceof File)) {
                data.append("image", file.file || file);
            }

            const config = {headers: {"Content-Type": "multipart/form-data"}};

            const response = locationId
                ? await api.patch(`/api/v1.0/locations/${locationId}/`, data, config)
                : await api.post(`/api/v1.0/locations/`, data, config);

            onSuccess?.(response.data);
            toast.success(t(locationId ? "Data updated successfully" : "Location created successfully"));
            onClose?.();
        } catch (error) {
            setErrors(error.response?.data || {});
        }
    });

    const handleDelete = async () => {
        const confirmed = window.confirm(t("Are you sure you want to delete this location?"));
        if (!confirmed) return;

        try {
            await api.delete(`/api/v1.0/locations/${locationId}/`);
            toast.success(t("Location deleted successfully"));
            onSuccess?.();
            onClose?.();
        } catch (error) {
            console.error("Delete error", error);
            toast.error(t("Error deleting location"));
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <h2>
                <FeatherIcon icon="map-pin"/>
                {locationId ? t("Edit location") : t("Creating a location")}
            </h2>
            <div className="form-group">
                <label>{t("name")}</label>
                {loader ? (
                    <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                ) : (
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control"
                />)}
                <ErrorMessage error={errors.name && t(errors.name)}/>
            </div>

            {/* Country Select */}
            <div className="form-group">
                {loader ? (
                    <>
                        <label>{t("country")}</label>
                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                    </>
                ) : (
                <SelectInput
                    default_text={t("Choose country")}
                    data={COUNTRY_OPTIONS}
                    default_value={formData.country || "UA"}
                    label_text={t("country")}
                    input_name="country"
                    setInputValue={value => setFormData(prev => ({...prev, country: value}))}
                    search={true}
                />)}
                <ErrorMessage error={errors.country && t(errors.country)}/>
            </div>
            {["city", "address"].map((field) => (
                <div key={field} className="form-group">
                    <label>{t(field)}</label>
                    {loader ? (
                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                    ) : (
                    <input
                        type="text"
                        name={field}
                        value={formData[field] ?? ""}
                        onChange={handleChange}
                        className="form-control"
                    />)}
                    <ErrorMessage error={errors[field] && t(errors[field])}/>
                </div>
            ))}
                <FormImageUpload
                    path="locations"
                    id={locationId}
                    file={file}
                    setFile={setFile}
                    width={(typeof file === 'object' && file?.width) || width}
                    height={(typeof file === 'object' && file?.height) || height}
                />
            {user?.is_superuser && (
                <div className="form-group">
                    <input
                        type="checkbox"
                        id="adminClubsCheck"
                        checked={formData.is_intennis_db}
                        onChange={e =>
                            setFormData(prev => ({...prev, is_intennis_db: e.target.checked}))
                        }
                    />
                    <label htmlFor="adminClubsCheck">
                        {t("intennis base")}
                    </label>
                </div>
            )}
            <br/>
            <ul className="tabs">
                <li>
                    <a href="#" className={`action-button ${isSubmitting ? "disabled" : ""}`} onClick={(e) => {
                        e.preventDefault();
                        if (isSubmitting) return;
                        e.currentTarget.closest('form').requestSubmit();
                    }}>
                        <FeatherIcon icon="check" /> {t("Save")}
                    </a>
                </li>
                    {locationId && (formData.creator === user.user_id || user?.is_superuser) &&
                        <li>
                        <a href="#" className="action-button" onClick={handleDelete}>
                            <FeatherIcon icon="trash-2" /> {t("Delete")}
                        </a>
                        </li>
                    }
            </ul>
            <br/>
        </form>
    );
};

export default LocationForm;
